import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  DeptAndAccountDefaultAccountFragment,
  DeptAndAccountDefaultDepartmentFragment,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import useGetOrganisationFlatAccounts from '@/modules/admin-center/hooks/useGetOrganisationFlatAccounts'
import { Button } from '@/modules/shared/components/button/Button'
import ComboboxServer from '@/modules/shared/components/combobox/server/ComboboxServer'
import { useGetDeptAndAccountDepartments } from '@/modules/shared/components/form-elements/hooks/useGetDeptAndAccountDepartments'
import { Tooltip } from '@/modules/shared/components/tooltip/Tooltip'
import { QuestionMarkIcon } from '@/modules/shared/icons/QuestionMarkIcon'
import { TrashIcon } from '@/modules/shared/icons/TrashIcon'
import { classNames } from '@/modules/shared/utils/classNames'
import { ignoreSingleCharacter } from '@/modules/shared/utils/ignoreSingleCharacter'

interface DeptAndAccountProps {
  defaultDepartmentValue?: DeptAndAccountDefaultDepartmentFragment | null
  defaultAccountValue?: DeptAndAccountDefaultAccountFragment | null
  showExplanation?: boolean
  disabled?: boolean
  title?: string
  description?: string
  required?: boolean
  className?: string
  onlyDepartments?: boolean
  onlyAccounts?: boolean
  showAsRow?: boolean
  departmentTooltip?: string
  accountTooltip?: string
  purchaserId?: number
  saveAsObject?: boolean
  floating?: boolean
  dontShowApSystemAccounts?: boolean
  showAsCurrentlySet?: boolean
  setRemoveDefaults?: Dispatch<SetStateAction<boolean>> // needs to be provided when showAsCurrentlySet is true
  removeDefaults?: boolean // needs to be provided when showAsCurrentlySet is true
  disableDepartment?: boolean
}

export default function DeptAndAccount({
  defaultAccountValue,
  defaultDepartmentValue,
  showExplanation = true,
  disabled,
  title,
  description,
  required = true,
  className,
  onlyDepartments,
  onlyAccounts,
  showAsRow,
  departmentTooltip,
  accountTooltip,
  purchaserId,
  saveAsObject = false,
  floating = false,
  dontShowApSystemAccounts = false,
  showAsCurrentlySet = false,
  setRemoveDefaults,
  removeDefaults,
  disableDepartment = false,
}: DeptAndAccountProps) {
  const { t } = useTranslation()
  const {
    getValues,
    control,
    setValue,
    formState: { errors },
  } = useFormContext()
  const [hasDefault, setHasDefault] = useState(false)
  const [resetAccount, setResetAccount] = useState(0)
  const { currentLegalEntity } = useSessionUser()
  const initializedRef = useRef(false)
  const [departmentSearch, setDepartmentSearch] = useState('')

  // Store default values in refs to prevent them from causing re-renders
  const defaultDeptRef = useRef(defaultDepartmentValue)
  const defaultAccRef = useRef(defaultAccountValue)

  // Use memoized values for organisationId to prevent re-renders
  const organisationId = useMemo(() => {
    if (onlyAccounts && currentLegalEntity?.id) {
      return Number(currentLegalEntity.id)
    }
    return defaultDeptRef.current?.id ? Number(defaultDeptRef.current.id) : undefined
  }, [onlyAccounts, currentLegalEntity?.id])

  const {
    departments,
    loadingStates: departmentLoadingStates,
    onFetchMore,
    networkStatus,
    pageInfo: departmentPageInfo,
  } = useGetDeptAndAccountDepartments({ organisationId: purchaserId, searchText: departmentSearch })
  const {
    fetchOrganisationFlatAccounts,
    flatAccounts,
    loadingStates: accountLoadingStates,
    networkStatus: accountNetworkStatus,
    onFetchMoreFlatAccounts,
    refetchApSystemFlatAccounts,
    refetchOrganisationFlatAccounts,
    accountsPayableSystemId,
    allAccountsDisabled,
    pageInfo: accountPageInfo,
  } = useGetOrganisationFlatAccounts({
    organisationId,
    lazyQuery: true,
    isParent: false,
    dontShowApSystemAccounts,
  })

  /**
   * Handle department selection
   * @param e - Selected department
   * @param field - Form field controller
   */
  const onSelectedDepartment = useCallback(
    (
      e: DeptAndAccountDefaultDepartmentFragment,
      field: ControllerRenderProps<FieldValues, 'departmentId' | 'department'>
    ) => {
      field.onChange(saveAsObject ? { id: e.id, name: e.name } : e.id)
      if (!hasDefault) {
        setValue(saveAsObject ? 'account' : 'accountId', null)
        fetchOrganisationFlatAccounts({
          variables: {
            organisationId: e.id,
            filters: { isParent: false },
          },
        })
        setResetAccount((prev) => prev + 1) // Increment to avoid same value
      } else {
        setHasDefault(false)
      }
    },
    [fetchOrganisationFlatAccounts, hasDefault, setValue, saveAsObject]
  )

  /**
   * Handle account search
   * @param e - Search text
   */
  const onSearchAccounts = useCallback(
    (e: string) => {
      const deptId = getValues(saveAsObject ? 'department.id' : 'departmentId')
      const filteredSearchValue = ignoreSingleCharacter(e)
      if (allAccountsDisabled && !dontShowApSystemAccounts) {
        refetchApSystemFlatAccounts({
          accountsPayableSystemId,
          filters: {
            searchText: filteredSearchValue,
            isParent: false,
          },
        })
      } else {
        refetchOrganisationFlatAccounts({
          organisationId: deptId,
          filters: {
            searchText: filteredSearchValue,
            isParent: false,
          },
        })
      }
    },
    [
      allAccountsDisabled,
      accountsPayableSystemId,
      getValues,
      refetchApSystemFlatAccounts,
      refetchOrganisationFlatAccounts,
      saveAsObject,
      dontShowApSystemAccounts,
    ]
  )

  const onRemoveDefaultsHandler = useCallback(() => {
    setValue(saveAsObject ? 'department' : 'departmentId', null, { shouldDirty: true })
    setValue(saveAsObject ? 'account' : 'accountId', null, { shouldDirty: true })
    setRemoveDefaults?.(true)
    setHasDefault(false)
  }, [setValue, saveAsObject, setRemoveDefaults])

  // Initialize form values once on component mount
  useEffect(() => {
    if (!initializedRef.current) {
      // Initialize department if provided
      if (defaultDeptRef.current?.id) {
        const deptId = Number(defaultDeptRef.current.id)
        setValue(saveAsObject ? 'department' : 'departmentId', saveAsObject ? defaultDeptRef.current : deptId, {
          shouldValidate: false,
          shouldDirty: false,
        })

        // Initialize account if provided
        if (defaultAccRef.current?.id) {
          setValue(
            saveAsObject ? 'account' : 'accountId',
            saveAsObject ? defaultAccRef.current : Number(defaultAccRef.current.id),
            { shouldValidate: false, shouldDirty: false }
          )
        }

        // Fetch accounts
        if (!onlyDepartments) {
          fetchOrganisationFlatAccounts({
            variables: { organisationId: deptId, filters: { isParent: false } },
          })
        }
        setHasDefault(true)
      } else if (onlyAccounts && currentLegalEntity?.id) {
        // Only fetch accounts when showing accounts only
        fetchOrganisationFlatAccounts({
          variables: { organisationId: Number(currentLegalEntity.id), filters: { isParent: false } },
        })
      }

      initializedRef.current = true
    }
  }, [])

  // Memoize default values to avoid re-renders
  const memoizedDeptValue = useMemo(() => defaultDeptRef.current, [])
  const memoizedAccValue = useMemo(() => defaultAccRef.current, [])

  // Determine if we should show the "currently set" display
  const hasDefaults = defaultDepartmentValue || defaultAccountValue
  const showCurrentlySetDisplay = showAsCurrentlySet && hasDefaults && !removeDefaults

  // Only show default values in comboboxes if we haven't removed them
  const shouldShowDefaults = !showAsCurrentlySet || !removeDefaults

  return (
    <section
      className={classNames(className, { 'mt-8': showExplanation && !className && !showAsRow })}
      data-testid="department-and-account"
    >
      {showExplanation && (
        <>
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="text-sm text-gray-500">{description}</p>
        </>
      )}
      <div className={classNames({ 'mt-4': showExplanation })}>
        {showCurrentlySetDisplay && (
          <section
            className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-gray-200 p-3"
            data-testid="currently-set-dept-account"
          >
            <div>
              {defaultDepartmentValue && !onlyAccounts && <span>{defaultDepartmentValue.name}</span>}
              {defaultDepartmentValue && defaultAccountValue && !onlyAccounts && !onlyDepartments && (
                <span> {'>'} </span>
              )}
              {defaultAccountValue && !onlyDepartments && <span>{defaultAccountValue.codedName}</span>}
            </div>
            <div className="flex items-center gap-x-1 text-gray-500">
              <span>{t('general.currentlySet', 'Currently Set')}</span>
              <Tooltip
                content={t('general.currentlySetDeptAccountTooltip', 'The Department and Account currently set.')}
              >
                <QuestionMarkIcon className="size-4" />
              </Tooltip>
              {!disabled && (
                <Tooltip
                  content={t(
                    'general.removeCurrentlySetDeptAccountTooltip',
                    'Remove the currently set Department and Account.'
                  )}
                >
                  <Button
                    type="button"
                    data-testid="remove-defaults"
                    className="flex items-center justify-center"
                    onClick={() => !disabled && onRemoveDefaultsHandler()}
                  >
                    <TrashIcon className="size-7" />
                  </Button>
                </Tooltip>
              )}
            </div>
          </section>
        )}
        {(!showAsCurrentlySet || removeDefaults || !hasDefaults) && (
          <div
            className={classNames('w-full', {
              'mt-3': showExplanation && !showAsRow && !showCurrentlySetDisplay,
              'flex gap-x-4': showAsRow,
              'space-y-3': !showAsRow,
            })}
          >
            {!onlyAccounts && (
              <div className={classNames({ 'w-1/2': showAsRow })}>
                {showAsRow && (
                  <p className="mb-1.5 flex gap-x-1.5 font-bold">
                    {t('general.department', 'Department')}
                    <Tooltip content={departmentTooltip || ''}>
                      <QuestionMarkIcon className="size-4" />
                    </Tooltip>
                  </p>
                )}
                <Controller
                  control={control}
                  name={saveAsObject ? 'department' : 'departmentId'}
                  rules={{ required }}
                  render={({ field }) => (
                    <ComboboxServer
                      testId="department"
                      defaultValue={shouldShowDefaults ? memoizedDeptValue : undefined}
                      loading={departmentLoadingStates.loading}
                      placeholder={t('general.selectADepartmentDots', 'Select a Department...')}
                      keyExtractor={(e) => String(e?.id)}
                      items={departments?.map((dept) => ({ id: dept.id, name: dept.name })) || []}
                      onSelected={(e) => e && onSelectedDepartment(e, field)}
                      hasError={Boolean(saveAsObject ? errors.department : errors.departmentId)}
                      disabled={disabled || disableDepartment}
                      floating={floating}
                      onDisplay={(e) => e?.name || ''}
                      onInputChange={(e) => setDepartmentSearch(e)}
                      networkStatus={networkStatus}
                      onFetchMore={onFetchMore}
                      hasMore={!!departmentPageInfo?.hasNextPage}
                    />
                  )}
                />
              </div>
            )}
            {!onlyDepartments && (
              <div className={classNames({ 'w-1/2': showAsRow })}>
                {showAsRow && (
                  <p className="mb-1.5 flex gap-x-1.5 font-bold">
                    {t('general.account', 'Account')}
                    <Tooltip content={accountTooltip || ''}>
                      <QuestionMarkIcon className="size-4" />
                    </Tooltip>
                  </p>
                )}
                <Controller
                  control={control}
                  name={saveAsObject ? 'account' : 'accountId'}
                  rules={{ required }}
                  render={({ field }) => (
                    <ComboboxServer
                      testId="account"
                      defaultValue={shouldShowDefaults ? memoizedAccValue : undefined}
                      loading={accountLoadingStates.setVariablesLoading}
                      disabled={
                        (disabled || (saveAsObject ? !getValues('department.id') : !getValues('departmentId'))) &&
                        !onlyAccounts
                      }
                      placeholder={t('createRequisition.deptAndAccount.placeholder.account', 'Select an Account...')}
                      keyExtractor={(e) => String(e?.id)}
                      items={flatAccounts.map((account) => ({ id: account.id, codedName: account.codedName }))}
                      onSelected={(e) =>
                        field.onChange(saveAsObject ? { id: e?.id, codedName: e?.codedName } : e?.id, {
                          shouldDirty: true,
                        })
                      }
                      hasError={Boolean(saveAsObject ? errors.account : errors.accountId)}
                      errorMessage={t(
                        'createRequisition.deptAndAccount.required.account',
                        'You must select a Department and an Account.'
                      )}
                      resetSelectItem={resetAccount}
                      floating={floating}
                      networkStatus={accountNetworkStatus}
                      onDisplay={(e) => e?.codedName || ''}
                      onInputChange={onSearchAccounts}
                      onFetchMore={onFetchMoreFlatAccounts}
                      hasMore={!!accountPageInfo?.hasNextPage}
                    />
                  )}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
